import { UserOutlined } from "@ant-design/icons";
import { Attachments, Bubble, Prompts } from "@ant-design/x";
import { BubbleDataType } from "@ant-design/x/es/bubble/BubbleList";
import { Flex, GetProp, GetRef, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useToken } from "../../hooks/useToken";
import { useTheme } from "../../providers/ThemeProvider";
import { useAuth } from "../../providers/UserProvider";
import { getMediaFile } from "../../services/media.service";
import { getUserProfile } from "../../services/users.service";
import { CreateEventFunction } from "./functions/createEvent.function";

export function AIDrawerMessagesRender({
  messages,
}: {
  messages: BubbleDataType[];
}) {
  const { user } = useAuth();
  const { token } = useToken();
  const listRef = useRef<GetRef<typeof Bubble.List>>(null);

  const [avatarUrl, setAvatarUrl] = useState<string>("");

  const { theme } = useTheme();

  useEffect(() => {
    getUserProfile({
      onSuccess: (data) => {
        if (data.avatar) {
          getMediaFile({
            mediaId: data.avatar,
            onSuccess: (data) => {
              setAvatarUrl(URL.createObjectURL(data));
            },
          });
        }
      },
    });
  }, [user.uid]);

  const roles: GetProp<typeof Bubble.List, "roles"> = {
    greetings: {
      style: {
        width: "100%",
        borderStartStartRadius: 4,
        alignSelf: "stretch",
        padding: `${token.paddingSM}px, ${token.padding}px`,
      },
      styles: {
        content: {
          width: "100%",
          padding: "0",
          background:
            theme === "LIGHT"
              ? "linear-gradient(90deg, #F2F9FE 0%, #F7F3FF 100%)"
              : "linear-gradient(90deg, rgba(90, 196, 255, 0.12) 0%, rgba(174, 136, 255, 0.12) 100%)",
        },
      },
    },
    ai: {
      placement: "start",
      avatar: {
        icon: <img src={theme == "DARK" ? "/icon-light.svg" : "/icon-dark.svg"} />,
      },
      typing: { step: 5, interval: 20 },
      style: {
        maxWidth: 600,
      },
      styles: {
        content: {
          background: token.colorFillContent,
          borderRadius: `${token.borderRadiusXS}px, ${token.borderRadiusLG}px, ${token.borderRadiusLG}px, ${token.borderRadiusLG}px`,
          padding: ` ${token.paddingSM}, ${token.padding}px`,
          alignSelf: "stretch",
        },
      },
      messageRender: (content: any) => {
        const text: string = content.text || "";

        if (content.call?.name == "create_event" && content.call.args) {
          return <CreateEventFunction args={content.call.args} />;
        }

        return (
          <>
            {text.split("\n").map((item, key) => {
              return (
                <Typography
                  style={{
                    color: token.colorText,
                    fontSize: token.fontSize,
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: token.lineHeight,
                    padding: ` ${token.paddingSM}, ${token.padding}px`,
                  }}
                  key={key}
                >
                  {item}
                </Typography>
              );
            })}
          </>
        );
      },
    },
    user: {
      styles: {
        content: {
          background: token.colorFillQuaternary,
          borderRadius: `${token.borderRadiusLG}px, ${token.borderRadiusXS}px, ${token.borderRadiusLG}px, ${token.borderRadiusLG}px`,
          padding: ` ${token.paddingSM}, ${token.padding}px`,
          alignSelf: "stretch",
        },
      },
      placement: "end",
      avatar: {
        src: avatarUrl,
        icon: <UserOutlined />,
        style: { background: "#87d068" },
      },
      messageRender: (content) => {
        return (
          <Typography
            style={{
              color: token.colorText,
              fontSize: token.fontSize,
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: token.lineHeight,
              padding: ` ${token.paddingSM}, ${token.padding}px`,
            }}
          >
            {(content as any).text || ""}
          </Typography>
        );
      },
    },
    suggestion: {
      placement: "start",
      variant: "borderless",
      style: {
        width: "100%",
      },
      styles: {
        content: {
          width: "100%",
          background:
            theme === "LIGHT"
              ? "linear-gradient(90deg, #F2F9FE 0%, #F7F3FF 100%)"
              : "linear-gradient(90deg, rgba(90, 196, 255, 0.12) 0%, rgba(174, 136, 255, 0.12) 100%)",
          padding: `${token.marginSM}px ${token.margin}px`,
          flexDirection: "column",
          alignItems: "flex-start",
          gap: token.paddingXXS,
          alignSelf: "stretch",
          borderRadius: token.borderRadiusLG,
        },
      },
      messageRender: (items) => (
        <Prompts
          vertical
          items={items as any}
          styles={{
            list: {
              width: "100%",
            },
            item: {
              width: "100%",
              padding: `${token.marginSM}px, ${token.margin}px`,
              borderRadius: token.borderRadiusLG,
              backgroundColor: token.colorFillQuaternary,
              fontSize: token.fontSize,
              color: token.colorText,
              fontWeight: 400,
              lineHeight: token.lineHeight,
            },
          }}
        />
      ),
    },
    file: {
      placement: "start",
      avatar: { icon: <UserOutlined />, style: { visibility: "hidden" } },
      variant: "borderless",
      messageRender: (items: any) => (
        <Flex vertical gap="middle">
          {(items as any[]).map((item) => (
            <Attachments.FileCard key={item.uid} item={item} />
          ))}
        </Flex>
      ),
    },
  };

  return (
    <>
      <Bubble.List
        autoScroll={true}
        ref={listRef}
        style={{ width: "100%" }}
        roles={roles}
        items={messages}
      />
    </>
  );
}
